<template>
	<div class="tooltip-container">
		<div
		class="slot"
		ref="slottedElement"
		@mouseover="(size === 'tiny' && !getMobileAppState) ? showTooltip = true : null"
		@mouseleave="(size === 'tiny' && !getMobileAppState) ? showTooltip = false : null">
			<slot name="slotElement" :onClick="clickOnSlotHandler"></slot>
		</div>
		<transition name="show-tooltip" @before-enter="beforeEnter">
			<div
			v-if="showTooltip"
			:class="'tooltip-' + size"
			class="tooltips-general"
			v-click-outside="() => { showTooltip = false }">
				<div v-if="description || title" class="small-row">
					<div class="small-text-fields">
						<!-- *********************************************
						*                  Description                   *
						********************************************** -->
						<div v-if="description && size !== 'medium'" class="description text-boxes-general" :class="{'negative': negative}">
							{{description}}
						</div>
						<!-- *********************************************
						*                      Title                     *
						********************************************** -->
						<div v-if="title && size !== 'tiny'" class="title text-boxes-general" :class="{'negative': negative}">
							{{title}}
						</div>
					</div>
					<!-- Icon for small sized tooltip only -->
					<div v-if="size === 'small'" class="arrow">
						<button-quaternary icon="arrow-right-full" @clicked="[showTooltip = false, emit('leftButtonClicked')]"/>
					</div>
				</div>
				<!-- *********************************************
				*                      Text                      *
				********************************************** -->
				<div v-if="text && size === 'medium'" class="text text-boxes-general" :class="{'negative': negative}">
					{{text}}
				</div>
				<!-- *********************************************
				*                     Buttons                    *
				********************************************** -->
				<div v-if="size === 'medium'" class="buttons" :class="{'negative': negative}">
                    <!--
                        This is to prevent hoveing on elements with tooltip hovering visually first element
                        why this happen? magic, but this solves it
                     -->
                    <button v-show="false"></button>
                    <!-- Left Button -->
                    <button-secondary
                    buttonSize="small"
                    :label="leftButtonText || t('tooltips.leftButton')"
                    @clicked="[showTooltip = false, emit('leftButtonClicked')]"/>
                    <!-- Right Button -->
                    <button-primary
                    buttonSize="small"
                    :label="rightButtonText || t('tooltips.rightButton')"
                    @clicked="[showTooltip = false, emit('rightButtonClicked')]"/>
				</div>
			</div>
		</transition>
	</div>
</template>

<script setup>
import ButtonPrimary from '@/components/generic/buttons/Primary'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import ButtonQuaternary from '@/components/generic/buttons/Quaternary'

/* Vue, Helpers Imports */
import { ref } from 'vue'
import { validateIfParentExists } from '@/assets/scripts/htmlManipulators.js'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['leftButtonClicked', 'rightButtonClicked'])

/** Props
@title {String} - tooltip title
@text {String} - tooltip text
@description {String} - tooltip description
@negative {Boolean} - prop to apply negative styles
@size {String} can be [medium, small, tiny (default)]
@leftButtonText {String} - text for left button, has default text if this is not passed
@rightButtonText {String} - text for right button, has default text if this is not passed
@applyPositionFixed {Boolean} - prop to force tooltip to use fixed position
*/

const props = defineProps({
	title: String,
	text: String,
	description: String,
	negative: Boolean,
	size: {
		type: String,
		default: 'tiny'
	},
	leftButtonText: String,
	rightButtonText: String,
	applyPositionFixed: Boolean
})

const showTooltip = ref(false)
/*************************************************
*                Open/Close Logic                *
*************************************************/
const slottedElement = ref('')

const TOOLTIP_SIZE = () => {
	switch (props.size) {
		case 'small': return 145
		case 'medium': return 320
		default: return 97
	}
}

const beforeEnter = (el) => {
	if (props.applyPositionFixed) {
		el.style.left = `${slottedElement.value.getBoundingClientRect().left}px`
		el.style.top = `${slottedElement.value.getBoundingClientRect().top}px`
		el.style.transform = `translate(-40%, calc(-${100}% - 8px))`
		el.style.position = 'fixed'
		return
	}
	/*************************************************
	*                Left/right Logic                *
	*************************************************/
	/* Gets Slotted element height to use in css positioning and adds 8px gap */
	el.style.setProperty('--slotElementHeight', (slottedElement.value.offsetHeight / 2 - 12 + 'px'))

	/* Distance from start and end of parent until slottedElement starting position */
	const startOfParentDistance = validateIfParentExists(slottedElement.value, 'content-wrapper').getBoundingClientRect().left
	const endOfParentDistance = validateIfParentExists(slottedElement.value, 'content-wrapper').getBoundingClientRect().right

	/* Slotted element distance from start of parent + half it's size returns middle of slotted element */
	const middleOfslotDistance = slottedElement.value.getBoundingClientRect().left + slottedElement.value.offsetWidth / 2
	/* Middle of slotted element position - half of tooltip size returns starting position of tooltip */
	const tooltipStartDistance = Math.round(middleOfslotDistance - TOOLTIP_SIZE() / 2)
	/* Middle of slotted element position - half of tooltip size returns ending position of tooltip */
	const tooltipEndDistance = Math.round(middleOfslotDistance + TOOLTIP_SIZE() / 2)

	/* Checks if tooltip is outside of parent div, moves it to the right/left and adds a 16px gap */
	if (tooltipStartDistance <= startOfParentDistance) {
		/* Distance outside of content-wrapper */
		const hiddenTooltipDistance = Math.round(startOfParentDistance - tooltipStartDistance)
		el.style.left = 'calc(50% + ' + hiddenTooltipDistance + 'px + 16px)'
	}

	if (tooltipEndDistance >= endOfParentDistance) {
		/* Distance outside of content-wrapper */
		const hiddenTooltipDistance = Math.round(tooltipEndDistance - endOfParentDistance)
		el.style.left = 'calc(50% - ' + hiddenTooltipDistance + 'px - 16px)'
	}
}

const clickOnSlotHandler = () => {
	setTimeout(() => {
		showTooltip.value = true
	})
}
</script>

<style lang="scss" scoped>
.tooltip-container {
	--slotElementHeight: 0;

	position: relative;
	align-self: flex-start;
}

.tooltips-general {
	top: 50%;
	left: 50%;
	transform: translate(-50%, calc(-100% - var(--slotElementHeight)));
	max-width: calc(100% - 40px);
	padding: 16px;

	display: flex;
	flex-direction: column;
	position: absolute;
	z-index: 1;

	border-radius: 8px;
	background-color: var(--secondary-light-4);
	box-shadow: 0px 6px 24px 0px rgba(0, 0, 0, 0.16);

	opacity: 1;
	transition: transform 0.3s linear, opacity 0.1s linear 0.1s;

	&.show-tooltip-enter-from,
	&.show-tooltip-enter,
	&.show-tooltip-leave-active {
		opacity: 0;
	}

	&.show-tooltip-active,
	&.show-tooltip-leave {
		opacity: 1;
	}

	.text-boxes-general {
		width: 100%;
		height: auto;

		letter-spacing: normal;

		&:not(.text) {
			display: flex;
			flex-direction: column;
			align-items: left;
		}
	}

	.description {
		@include H7-Regular;
		line-height: 1;
		color: var(--secondary-dark-1);
	}

	.title {
		@include H6-Medium;
		line-height: 1.125;
		color: var(--secondary-dark-4);
	}

	.text {
		@include H6-Regular;
		line-height: 1.125;
		margin-bottom: 16px;
		color: var(--secondary-dark-4);
	}
}

.tooltip-tiny {
	min-width: 97px;
}

.tooltip-small {
	min-width: 145px;

	.small-row {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 8px;

		.small-text-fields {
			display: flex;
			flex-direction: column;
			gap: 4px;
		}

		.arrow {
			width: 40px;
			height: 40px;
			display: flex;
			align-items: center;
			justify-content: center;
			overflow: hidden;

			svg {
				width: 16px;
				height: 16px;

				:deep(.change-color-fill) {
                    fill: var(--secondary-dark-4);
				}
			}
		}
	}
}
.tooltip-medium {
	min-width: 320px;

	.buttons {
		display: flex;
		justify-content: space-between;
	}
}
</style>
